import { useDebounceFn, useTitle } from 'ahooks'
import ActionConfirmationInputDialog from 'live/components/actions/ui/confirmation-input-dialog'
import SaveButton from 'live/components/forms/ui/button-save'
import AdminHeader from 'live/components/header/ui/admin/header'
import LiveTooltip from 'live/components/ui/tooltip'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'
import Router from 'live/services/router'
import lodash from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { PackageResource, PackageUpdateInput } from '../http/api'
import { liveHttpServicesClient, packageManagerHttpServicesClient, useOnRemoteChanges } from '../http/services'
import FeaturesCard from './FeaturesCard'
import ChannelsCard from './PackageChannels'
import PluginsCard from './PackagePlugins'
import { Active } from './styles'
import { PackageStatusTag } from './Tag'
import { thereAreDependencies } from './utils'

const PackagePage: React.VFC<{ urlParams: string[] }> = props => {
    const id: string = props.urlParams[0]
    const [data, setData] = useState<PackageResource>(null)
    const [error, setError] = useState(null)
    const [initialLoading, setInitialLoading] = useState(false)
    const [userInput, setUserInput] = useState({} as PackageUpdateInput) // keeps only new changed input
    const form = getFormData(data, userInput)

    const packageName = data?.title || data?.name || 'N/A'
    useTitle(`Live: Package ${data ? packageName : id}`, { restoreOnUnmount: true })

    const fetchData = useCallback(() => {
        return new Promise((resolve, reject) => {
            packageManagerHttpServicesClient
                .getPackage(id)
                .then(setData)
                .then(resolve)
                .catch(response => {
                    setError(response)
                    reject(response)
                })
        })
    }, [id])

    useEffect(() => {
        setInitialLoading(true)
        fetchData()
            .then(() => {
                setUserInput({} as PackageUpdateInput) // clear it
            })
            .catch(response => {
                setError(response)
            })
            .finally(() => setInitialLoading(false))
    }, [id, fetchData])

    const { run: refetch } = useDebounceFn(fetchData)
    useOnRemoteChanges(refetch)

    useEffect(() => {
        if (error instanceof Response) {
            if (error.status === 404) {
                Router.setHash('packages/')
            }
            globalNotification.error(error.statusText)
        } else if (error) {
            globalNotification.error(error.message)
        }
    }, [error])

    const [showConfirmationModal, setShowConfirmationModal] = useState(false)
    const [saved, setSaved] = useState(false)

    const submitDeleteRequest = useCallback(async () => {
        setIsUpdating(true)
        try {
            const pluginIdExists = !!data?.pluginId || data?.pluginId === 0
            if (pluginIdExists) {
                await liveHttpServicesClient.deletePlugin(data.pluginId)
            }
            await packageManagerHttpServicesClient.unregisterPackage(id)

            if (pluginIdExists) {
                globalNotification.success(i18n('Package successfully deleted.'))
            } else {
                globalNotification.warn(
                    i18n('missing_plugin_id_msg', { plugin: data?.pluginState?.description || data?.name }),
                    0,
                    true,
                    'sm',
                    { neverClose: true }
                )
            }
            Router.setHash('packages/')
        } catch (error) {
            globalNotification.error(i18n('Sorry, it was not possible to delete the package settings.'))
            console.error(error)
        } finally {
            setIsUpdating(false)
        }
    }, [data?.name, data?.pluginId, data?.pluginState?.description, id])

    const handlePackageEnable = useCallback(() => {
        setSaved(false)
        setUserInput(st => ({
            ...st,
            enabled: true
        }))
    }, [])

    const handlePackageDisable = useCallback(() => {
        setSaved(false)
        setUserInput(st => ({
            ...st,
            enabled: false
        }))
    }, [])

    const setFeatureEnabled = useCallback((uuid, enabled) => {
        setSaved(false)
        setUserInput(st => ({
            ...st,
            featuresEnabled: { ...st.featuresEnabled, [uuid]: Boolean(enabled) }
        }))
    }, [])

    const setFeatureSettings = useCallback((uuid, settings) => {
        setSaved(false)
        setUserInput(st => ({
            ...st,
            featuresSettings: { ...st.featuresSettings, [uuid]: settings }
        }))
    }, [])

    const [isUpdating, setIsUpdating] = useState(false)
    const submitUpdateRequest = useCallback(async () => {
        setIsUpdating(true)

        try {
            const response = await packageManagerHttpServicesClient.updatePackage(data.uuid, {
                ...form
            })

            if (response != null) {
                setSaved(true)
                setData(response)
                setUserInput({} as PackageUpdateInput) // seems best to clear user form state after saving, which will force seeing state from server

                globalNotification.success(i18n('Package settings successfully updated.'))
            }
        } catch (error) {
            setSaved(false)
            globalNotification.error(i18n('Sorry, it was not possible to apply changes to package at this moment.'))
            console.error(error)
            refetch()
        } finally {
            setIsUpdating(false)
        }
    }, [data?.uuid, form])

    const tag = data ? <PackageStatusTag package={data} /> : null
    const featuresCards = []
    const featuresByType = lodash.groupBy(data?.features, x => x.type)
    const sortedTypes = Object.keys(featuresByType).sort()
    sortedTypes.forEach(type => {
        const features = featuresByType[type]

        featuresCards.push(
            <FeaturesCard
                key={type}
                type={type}
                metadata={data.typesMetadata[type]}
                features={features}
                form={form}
                pkgOriginallyEnabled={!!data?.enabled}
                setFeatureEnabled={setFeatureEnabled}
                setFeatureSettings={setFeatureSettings}
            />
        )
    })

    const body = (
        <PackageDetails className={`admin-body admin-form ${initialLoading ? 'loading' : null}`}>
            {data ? (
                <>
                    <div className={`instance-header ${data?.iconBase64 ? '' : 'no-image'}`}>
                        <div className="pull-left">
                            {data?.iconBase64 ? (
                                <img
                                    className="package-icon"
                                    src={`data:image/png;base64,${data?.iconBase64}`}
                                    alt="package icon"
                                />
                            ) : null}
                            <div className="pull-right title-container">
                                <h2 className={`section-title ${data.version ? '' : 'no-subtitle'}`}>{packageName}</h2>
                                {data.version ? <h4 className="section-subtitle regular">v{data.version}</h4> : null}
                            </div>
                        </div>
                    </div>
                    <PackageSettings>
                        <div className="general-settings">
                            <h2>{i18n('General settings')}</h2>
                            <label>{i18n('Status')}</label>
                            <div className="package-status">
                                <label className="radio regular" onClick={handlePackageEnable}>
                                    <input type="radio" name="enabled" checked={form.enabled} />
                                    <Active color="var(--green-700)">
                                        <i className="far fa-check-circle" /> {i18n('Active')}
                                    </Active>
                                </label>
                                <label className="radio regular" onClick={handlePackageDisable}>
                                    <input type="radio" name="enabled" checked={!form.enabled} />
                                    <Active color="var(--gray-600)">
                                        <i className="far fa-power-off" /> {i18n('Inactive')}
                                    </Active>
                                </label>
                            </div>
                            <div className="package-validation">
                                <label>{i18n('Validation')}</label>
                                <div>{tag}</div>
                            </div>
                        </div>

                        {featuresCards.length > 0 ? (
                            <div>
                                <h2>{i18n('Features')}</h2>
                                {featuresCards}
                            </div>
                        ) : null}

                        {thereAreDependencies(data) && (
                            <div>
                                <h2>{i18n('Dependencies')}</h2>
                                <PluginsCard package={data} />
                                <ChannelsCard package={data} />
                            </div>
                        )}
                    </PackageSettings>
                </>
            ) : (
                i18n('There was an error loading this package')
            )}

            <div className="instance-footer-container package-footer">
                <div className="instance-footer">
                    <SaveButton
                        className="btn-lg btn-bold"
                        disabled={isUpdating || !data}
                        saving={isUpdating}
                        saved={saved}
                        // mustSave={this.props.mustSave}
                        message={i18n('Package successfully saved')}
                        onClick={submitUpdateRequest}
                    />

                    <span>
                        <a className="pull-right remove-instance" onClick={() => setShowConfirmationModal(true)}>
                            <LiveTooltip position="top" content={i18n('Delete instance')}>
                                <i className="far fa-trash-alt fa-fw" />
                            </LiveTooltip>
                        </a>

                        <ActionConfirmationInputDialog
                            show={showConfirmationModal}
                            onHide={() => setShowConfirmationModal(false)}
                            className="confirmation-input-modal modal-error modal-sm"
                            confirmationText={i18n('Remove')}
                            content={
                                <div className="modal-confirmation-alert">
                                    <p>
                                        {i18n('Are you sure you want remove the package "%{packageName}"', {
                                            packageName: data ? packageName : id
                                        })}
                                        ?
                                    </p>
                                    <strong className="msg-error">
                                        {i18n(
                                            'This action cannot be undone and the associated plugin will also be removed'
                                        )}
                                        .
                                    </strong>
                                    <p>
                                        <span>{i18n('If you really want to remove it, type')}</span>
                                        <span className="remove-text">{' ' + i18n('Remove') + ' '} </span>
                                        <span>{i18n('in the field below before completing the action')}:</span>
                                    </p>
                                </div>
                            }
                            btnText={i18n('Remove package')}
                            apply={submitDeleteRequest}
                        />
                    </span>
                </div>
            </div>
        </PackageDetails>
    )

    return (
        <div className="admin-content">
            <div className="admin-inner">
                <AdminHeader>
                    <h2>
                        <span className="breadcrumb-small">
                            <a href={'#/packages'}>{i18n('Packages')}</a>
                        </span>{' '}
                        / {data ? packageName : id}
                    </h2>
                </AdminHeader>
                {body}
            </div>
        </div>
    )
}

// merge user state input with data from resource to yield current form state
// `input` keeps only new input from this session
function getFormData(data: PackageResource | null, input: Partial<PackageUpdateInput>): PackageUpdateInput {
    const form = {
        enabled: input.enabled != null ? input.enabled : data?.enabled || false,

        featuresEnabled:
            data?.features == null
                ? {}
                : data.features.reduce((result, feature) => {
                      if (input?.featuresEnabled?.[feature.uuid] != null) {
                          result[feature.uuid] = input.featuresEnabled[feature.uuid]
                      } else {
                          result[feature.uuid] = feature.enabled || feature.status?.flags?.includes('INSTALLED') // handle inconsistent information by considering it on
                      }

                      return result
                  }, {}),

        featuresSettings:
            data?.features == null
                ? {}
                : data.features.reduce((result, feature) => {
                      if (input?.featuresSettings?.[feature.uuid] != null) {
                          result[feature.uuid] = input.featuresSettings[feature.uuid]
                      } else {
                          result[feature.uuid] = feature.settings
                      }

                      return result
                  }, {})
    }

    return form
}

const PackageDetails = styled.div`
    width: 100%;
    padding: 25px;

    .instance-footer-container {
        width: 100%;

        .instance-footer {
            width: 100%;
            margin-bottom: 12px;
        }
    }
`

const PackageSettings = styled.div`
    width: 100%;

    h2 {
        font-family: var(--font-semibold);
        font-size: 16px;
        line-height: 18px;
        text-transform: uppercase;
        color: var(--gray-800);
        margin-top: 0;
        margin-bottom: 20px;
    }

    .general-settings {
        padding: 32px 0;

        label {
            display: block;
            color: var(--gray-600);
            font-size: 12px;
            line-height: 16px;
            padding-bottom: 8px;
            font-family: var(--font-bold);
            text-transform: uppercase;
        }

        .package-status {
            display: flex;
            margin-bottom: 20px;

            label {
                cursor: pointer;
                margin-right: 35px;
                display: flex;
                align-items: center;

                input {
                    margin-right: 8px;
                    width: 16px;
                    height: 16px;

                    &::after {
                        left: 3px;
                        top: 3px;
                        width: 8px;
                        height: 8px;
                    }
                }
            }
        }

        .package-validation {
            padding-bottom: 40px;
        }
    }
`

export default PackagePage
